import React from "react";
import Link from "next/link";
import {
  AlertV2,
  ButtonV2,
  CheckboxV2,
  InputV2,
  Panel,
  RadioGroupV2,
} from "@/components/ui-v2";
import { ProvisioningFlowHint } from "@/modules/provisioning";
import { usageModeOptions } from "./registerFormUtils";
import type { RegisterFormModel } from "./useRegisterForm";

type ViewProps = {
  form: RegisterFormModel;
  displayError: string;
  isLoading: boolean;
};
const disabled = (form: RegisterFormModel, isLoading: boolean) =>
  isLoading || form.registrationCompleted;

function IntroPanel() {
  return (
    <Panel className="border border-slate-200 bg-white/85 p-6 shadow-lg backdrop-blur">
      <p className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
        Hızlı başlangıç
      </p>
      <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900">
        İKAI HR ile hemen başlayın
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">
        Önce hesabınızı açın, ardından doğrulama ve hızlı kurulumla panele
        geçin. Resmi ve detaylı bilgiler ilk ekranda zorunlu değildir; yalnız
        ilgili modülleri kullanırken sizden istenir.
      </p>
      <div className="mt-6 space-y-3 text-sm">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
          1. Hesabınızı oluşturun
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
          2. E-posta adresinizi doğrulayın
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
          3. Panelden eksik kurulum adımlarını tamamlayın
        </div>
      </div>
      <div className="mt-6">
        <ProvisioningFlowHint
          title="Soft onboarding yaklaşımı"
          body="Hesabınız açıldıktan sonra doğrudan panele geçebilirsiniz. Eksik şirket veya profil bilgileri uygulama içinde yönlendirme olarak gösterilir."
        />
      </div>
      <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-sm font-semibold text-slate-800">
          Bu ekranda neleri topluyoruz?
        </p>
        <ul className="mt-3 space-y-2 text-sm text-slate-600">
          <li>• Yönetici hesabı için temel kimlik bilgileri</li>
          <li>• Şirket kullanımında organizasyon adı</li>
          <li>• Hesabınızı güvenle başlatacak şifre ve sözleşme onayı</li>
        </ul>
      </div>
    </Panel>
  );
}

function MethodPicker({ form }: Pick<ViewProps, "form">) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {(["email", "google"] as const).map((method) => (
        <ButtonV2
          key={method}
          type="button"
          onClick={() => form.setRegisterMethod(method)}
          className={`rounded-2xl border p-4 text-left transition ${form.registerMethod === method ? "border-indigo-500 bg-indigo-50 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"}`}
        >
          <p className="text-sm font-semibold text-slate-900">
            {method === "email"
              ? "E-posta ile kayıt ol"
              : "Google ile devam et"}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            {method === "email"
              ? "Klasik e-posta ve şifre ile hesap açın. Sonra isterseniz Google hesabınızı da bağlayabilirsiniz."
              : "Ad, soyad ve e-posta bilgileriniz Google’dan gelsin. Sadece organizasyon tercihinizi alıp hızlıca panele geçirelim."}
          </p>
        </ButtonV2>
      ))}
    </div>
  );
}

function EmailFields({
  form,
  isLoading,
}: Pick<ViewProps, "form" | "isLoading">) {
  const input = (
    field: "firstName" | "lastName" | "email" | "password" | "confirmPassword",
    label: string,
    props: Record<string, string> = {}
  ) => (
    <InputV2
      label={label}
      {...props}
      value={String(form.fieldValues[field])}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        form.handleFieldChange(field, event.target.value)
      }
      onBlur={() => form.handleFieldBlur(field)}
      disabled={disabled(form, isLoading)}
      errorText={form.fieldErrors[field]}
      successText={form.fieldSuccess[field]}
    />
  );
  return (
    <Panel className="border border-slate-200 bg-slate-50/60 p-4 md:p-5">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            Yönetici bilgileri
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Bu bilgiler hesabınızı açacak ilk yönetici için kullanılır.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {input("firstName", "Ad", {
            placeholder: "Örnek: Ayşe",
            autoComplete: "given-name",
          })}
          {input("lastName", "Soyad", {
            placeholder: "Örnek: Yılmaz",
            autoComplete: "family-name",
          })}
        </div>
        {input("email", "E-posta", {
          type: "email",
          placeholder: "someone@example.com",
          autoComplete: "email",
        })}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {input("password", "Şifre", {
            type: "password",
            placeholder: "Güçlü bir şifre belirleyin",
            autoComplete: "new-password",
          })}
          {input("confirmPassword", "Şifre tekrar", {
            type: "password",
            placeholder: "Şifrenizi tekrar girin",
            autoComplete: "new-password",
          })}
        </div>
        <p className="text-xs text-slate-600">{form.passwordStrengthMessage}</p>
      </div>
    </Panel>
  );
}

function OrganizationFields({
  form,
  isLoading,
}: Pick<ViewProps, "form" | "isLoading">) {
  const isBusiness = form.usageMode === "BUSINESS";
  return (
    <Panel className="border border-slate-200 bg-slate-50/60 p-4 md:p-5">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            Kullanım ve organizasyon bilgileri
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Şirket kullanımında şirket adını şimdi alırız; diğer detaylar
            onboarding ekranında tamamlanır.
          </p>
        </div>
        <RadioGroupV2
          name="usageMode"
          label="Kullanım tipi"
          value={form.usageMode}
          onValueChange={form.handleUsageModeChange}
          options={usageModeOptions}
          direction="vertical"
        />
        <InputV2
          label={isBusiness ? "Şirket adı" : "Organizasyon adı"}
          placeholder={
            isBusiness
              ? "Örnek: İKAI Teknoloji A.Ş."
              : "İsterseniz daha sonra da girebilirsiniz"
          }
          autoComplete="organization"
          value={form.organizationName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            form.handleFieldChange("organizationName", event.target.value)
          }
          onBlur={() => form.handleFieldBlur("organizationName")}
          disabled={disabled(form, isLoading)}
          helperText={
            isBusiness
              ? "Çalışan daveti ve organizasyon kurulumu için bu alan zorunludur."
              : "Bireysel kullanımda bu alanı daha sonra tamamlayabilirsiniz."
          }
          errorText={form.fieldErrors.organizationName}
          successText={form.fieldSuccess.organizationName}
        />
      </div>
    </Panel>
  );
}

function ConsentAndSubmit({
  form,
  isLoading,
}: Pick<ViewProps, "form" | "isLoading">) {
  const google = form.registerMethod === "google";
  return (
    <>
      <Panel className="border border-slate-200 bg-slate-50/60 p-4 md:p-5">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Onay ve hızlı kayıt
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Devam etmeden önce sözleşme onayınızı alıyoruz. Seçtiğiniz kayıt
              yöntemi bu onaydan sonra aktif olur.
            </p>
          </div>
          <CheckboxV2
            checked={form.termsAccepted}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              form.handleFieldChange("termsAccepted", event.target.checked)
            }
            disabled={disabled(form, isLoading)}
            label={
              <>
                <Link
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-indigo-700 underline underline-offset-2 hover:text-indigo-800"
                >
                  Kullanım Koşulları
                </Link>
                {"'nı ve "}
                <Link
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-indigo-700 underline underline-offset-2 hover:text-indigo-800"
                >
                  Gizlilik Politikası
                </Link>
                {"'nı okudum, kabul ediyorum."}
              </>
            }
          />
          {form.fieldErrors.termsAccepted ? (
            <p className="text-xs text-red-600">
              {form.fieldErrors.termsAccepted}
            </p>
          ) : form.fieldSuccess.termsAccepted ? (
            <p className="text-xs text-emerald-600">
              {form.fieldSuccess.termsAccepted}
            </p>
          ) : null}
          {google ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-sm font-semibold text-slate-900">
                Google ile hızlı kayıt
              </p>
              <p className="mt-1 text-sm text-slate-600">
                Google hesabınız doğrulanmışsa sizi doğrudan panele alırız.
                Şirket detaylarını ve profilinizi daha sonra onboarding
                adımlarında tamamlayabilirsiniz.
              </p>
              {form.termsAccepted ? (
                <ButtonV2
                  type="button"
                  onClick={form.handleGoogleRegister}
                  className="mt-4 inline-flex w-full items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
                >
                  Google ile kayıt ol
                </ButtonV2>
              ) : (
                <AlertV2 variant="info" className="mt-4">
                  Google ile devam etmek için önce sözleşmeleri onaylayınız.
                </AlertV2>
              )}
            </div>
          ) : null}
        </div>
      </Panel>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {google ? (
          <div className="text-sm text-slate-600">
            Google ile hızlı kayıt seçili. Devam etmek için yukarıdaki Google
            butonunu kullanın.
          </div>
        ) : (
          <ButtonV2
            type="submit"
            size="lg"
            disabled={disabled(form, isLoading)}
            className="w-full sm:w-auto"
          >
            {isLoading ? "Hesap hazırlanıyor..." : "Ücretsiz hesabımı oluştur"}
          </ButtonV2>
        )}
        <p className="text-sm text-slate-600">
          Zaten hesabınız var mı?{" "}
          <Link
            href="/login"
            className="font-semibold text-indigo-700 hover:text-indigo-800"
          >
            Giriş yapın
          </Link>
        </p>
      </div>
    </>
  );
}

function RegisterForm({ form, displayError, isLoading }: ViewProps) {
  return (
    <Panel className="border border-slate-200 bg-white p-6 shadow-xl md:p-8">
      <form onSubmit={form.handleSubmit} className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">
            Ücretsiz hesap oluşturun
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Tek hesabı ister e-posta ve şifreyle, ister Google ile
            başlatabilirsiniz. Eksik kurulum adımlarını panel içinde daha sonra
            tamamlayabilirsiniz.
          </p>
        </div>
        <MethodPicker form={form} />
        {displayError ? (
          <AlertV2 variant="error" title="Kayıt tamamlanamadı">
            {displayError}
          </AlertV2>
        ) : null}
        {form.successMessage ? (
          <AlertV2 variant="success" title="Kayıt tamamlandı">
            {form.successMessage}
          </AlertV2>
        ) : null}
        {form.registerMethod === "email" ? (
          <EmailFields form={form} isLoading={isLoading} />
        ) : (
          <Panel className="border border-slate-200 bg-slate-50/60 p-4 md:p-5">
            <h3 className="text-lg font-semibold text-slate-900">
              Google ile hızlı başlangıç
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Google hesabınızın doğrulanmış ad, soyad ve e-posta bilgisini
              kullanacağız. Şifre sormadan hesabınızı açıp onboarding’e
              geçeceğiz.
            </p>
          </Panel>
        )}
        <OrganizationFields form={form} isLoading={isLoading} />
        <ConsentAndSubmit form={form} isLoading={isLoading} />
      </form>
    </Panel>
  );
}

export function RegisterView(props: ViewProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-indigo-50 to-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto grid w-full max-w-6xl gap-6 lg:grid-cols-[0.9fr_1.4fr]">
        <IntroPanel />
        <RegisterForm {...props} />
      </div>
    </div>
  );
}
